import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { VirtualElement } from "@popperjs/core/lib/types";
import { PopperWrapper } from "./PopperWrapper";
import ToolButton from "./ToolButton";
import { CommentType } from "./Annotations";
import D3Drag, { Point } from "./D3Drag";

export interface Props {
	/** Unique id of the currently editing comment. */
	id: number;
	/** Type of the currently editing comment. */
	type: CommentType;
	/** Where to attach to. */
	anchor: Element | VirtualElement;
	/** Text of the currently editing comment. */
	text: string;
	/** Close the popup. */
	close(): void;
	/** Go to the previous comment in this bunch.  `undefined` if there is none. */
	prev?(): void;
	/** Go to the next comment in this bunch.  `undefined` if there is none. */
	next?(): void;
	/** Update the text.  `undefined` if this comment cannot be edited. */
	changeText?(newText: string): void;
	/** Remove the comment.  `undefined` if this comment cannot be removed. */
	delete?(): void;
}

const POPUP_SPACING: [number, number] = [-10, 10];

export function CommentViewer(props: Props) {
	// If we're editing a comment within an overlap, highlight that individual content.
	const styles = useRef<HTMLStyleElement>(null!);
	const focusArea = useRef<HTMLTextAreaElement>(null!);

	useEffect(() => {
		if (props.text === "" && !!props.changeText) {
			const element = focusArea.current;
			element.focus({ preventScroll: true });
			const cursorPos = element.value.length;
			element.setSelectionRange(cursorPos, cursorPos);
		}
	}, []);

	useEffect(() => {
		const elt = document.createElement("style");
		document.head.appendChild(elt);
		styles.current = elt;
		return () => {
			document.head.removeChild(elt);
			styles.current = null!;
		};
	}, []);

	useEffect(() => {
		styles.current!.textContent = `
			[data-shard-of-${props.id}] {
				border-color: var(--active-annotation-comment-${props.type}-border-color) !important;
				background-color: var(--active-annotation-comment-${props.type}-background-color) !important;
			}
		`;
	}, [props.id, props.type]);

	const [displacement, changeDisplacement] = useState<Point>({ x: 0, y: 0 });
	const subject = () => displacement;
	const drag = changeDisplacement;

	return (
		<PopperWrapper
			placement="bottom-start"
			anchor={props.anchor}
			offset={POPUP_SPACING}
			displacement={displacement}
			oneShot>
			<div className="comment-viewer-popup">
				<div className="title-bar">
					<D3Drag subject={subject} drag={drag}>
						<div className="title">Comments</div>
					</D3Drag>
					<div className="toolbar">
						<ToolButton active={false} onClick={() => props.close()} title="Close">
							<span className="fas fa-times" />
						</ToolButton>
					</div>
				</div>
				<textarea
					ref={focusArea}
					value={props.text}
					readOnly={!props.changeText}
					onChange={props.changeText && ((e) => props.changeText!(e.target.value))}
				/>
				<div className="toolbar">
					<div className="button-group">
						{(props.prev || props.next) && (
							<>
								<ToolButton
									active={false}
									onClick={props.prev && (() => props.prev!())}
									disabled={!props.prev}
									title="Previous Comment">
									<span className="fas fa-chevron-left" />
								</ToolButton>
								<ToolButton
									active={false}
									onClick={props.next && (() => props.next!())}
									disabled={!props.next}
									title="Next Comment">
									<span className="fas fa-chevron-right" />
								</ToolButton>
							</>
						)}
					</div>
					<div className="button-group">
						{props.delete && (
							<ToolButton
								active={false}
								onClick={() => props.delete!()}
								title="Delete Comment">
								<span className="fas fa-trash" />
							</ToolButton>
						)}
					</div>
				</div>
			</div>
		</PopperWrapper>
	);
}
